import { ActivatedRoute, Router } from '@angular/router';
import { AfterContentInit, Component, ElementRef, NgZone, OnInit, ViewChild, AfterViewInit, } from '@angular/core';
import { buildView, showHideWaitMedic } from 'src/app/utils/waitMedic';
import { clearLocalStorage } from 'src/app/utils/utils';
import { DialogService } from 'src/app/services/dialog.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MangeStadisticsService } from 'src/app/services/manage-stadistics.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { OpentokService } from 'src/app/services/opentok.service';
import { roomDTO } from 'src/app/models/RoomDTO.model';
import { TokboxCredentials } from 'src/app/types/TokboxCredentials';
import { VideoconsultaBackendService } from 'src/app/services/videoconsulta-backend.service';

@Component({
  selector: 'app-videoconsulta',
  templateUrl: './videoconsulta.component.html',
  styleUrls: ['./videoconsulta.component.css'],
})
export class VideoconsultaComponent implements AfterViewInit, AfterContentInit {
  @ViewChild('videosWrapper', { static: true }) videosWrapper: ElementRef;
  @ViewChild('footer', { static: true }) footer: ElementRef;

  cid: number;
  isMedic: number;
  record: number;
  session: OT.Session;
  medicArrived: boolean;
  activeHangup: boolean;
  showTimer: boolean = false;
  showButtons: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private _videoconsulta: VideoconsultaBackendService,
    private _openTok: OpentokService,
    private router: Router,
    private _stadistics: MangeStadisticsService,
    private _dialog: DialogService,
    private _spinner: NgxSpinnerService,
    private ngZone: NgZone
  ) {}

  
  ngAfterViewInit(): void { 
    this.route.queryParams.subscribe((params) => {
      let payload = params['p'];

      if (payload) {
        payload = payload.replace(/\s/g, '+');
      }

      this._videoconsulta.sendPayload(payload).subscribe(({ cid, r, m }) => {
        this.cid = cid;
        this.isMedic = m;
        this.record = r;

        if (this.record !== 0 && this.record !== 1) {
          this.router.navigate( ['/finalizado'], { state: { cid: this.cid, m: this.isMedic } } );
          return;
        }

        if (!this.isMedic) {
          buildView()
          showHideWaitMedic(true)
        } 

        this.checkCid();
      });
    })

    if (this.router.url.includes('videoconsulta')) {
      window.onpopstate = async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        this._dialog.createDialog('finish', this.cid, this.isMedic);
      };
    }
  }

  private checkCid() {
    this.activeHangup = this.isMedic ? false : true;

    this._spinner.show();
    this._videoconsulta.checkCid(this.cid).subscribe({
      next: () => {
        this.startVC();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 400) {
          this._spinner.hide();
          this.router.navigate(['/finalizado'], {
            state: { cid: this.cid, m: this.isMedic },
          });
        }
      },
    });
  }

  ngAfterContentInit(): void {
    history.pushState(null, '', location.href);
  }

  async startVC(): Promise<void> {
    this._stadistics.initStadistics(this.cid, this.isMedic);

    const credentials = await this._videoconsulta.getTokboxCredentials(this.cid, this.isMedic, new roomDTO(0)).toPromise();
    
    if (this.record === 1) {
      if (this.isMedic) {
        this._spinner.hide();
        if (localStorage.getItem('consentRecorded') !== 'true') {
          await this._dialog.createDialog('consentR', this.cid, this.isMedic).afterClosed().toPromise()

          this._spinner.show();
          localStorage.setItem('consentRecorded', 'true');
        }
      } else {
        await this._videoconsulta.recordVC(this.cid).toPromise().catch((err) => console.log(err));
        this._spinner.hide();
        await this._videoconsulta.showConsentMessage(this.cid).toPromise()
          .catch(async (err: HttpErrorResponse) => {
            if (err.status === 404) await this._dialog.createDialog('consentR', this.cid, this.isMedic).afterClosed().toPromise();
          });

        this._spinner.show();
      }
    }
    
    this.initiOpentokSession(credentials);
  }

  async connectAndPublish(): Promise<void> {
    await this._openTok.connect().catch((err) => err && console.error(err));
    if (!this.isMedic) {
      this._videoconsulta.medicIsConnected(this.cid).toPromise().then(({ enter }) => {
        if (!enter) {
          this._dialog.createDialog('lateM', this.cid, this.isMedic);
          return;
        }

        this._openTok.publish(this.videosWrapper, this.isMedic).then(() => {
          showHideWaitMedic(false)
          this.footer.nativeElement.classList.add('show');
          this._stadistics.sendStatistics('inicio').catch((err) => console.log(err));
        }).catch((err: OT.OTError) => this.managePermissionsError(err) );
      })
      .catch((err) => err && console.error(err))
      .finally(() => this._spinner.hide());
    } else {
      this._openTok.publish(this.videosWrapper, this.isMedic).then(() => {
        this.showTimer = true;
        this.footer.nativeElement.classList.add('show');
        showHideWaitMedic(false)
        this._spinner.hide();

        if (localStorage.getItem('medicoConnected') === null) {
          this._videoconsulta.sendMedicStatus(this.cid).subscribe({
            next: () => {
              this.session.signal({ type: 'medicoConnected' }, (err) => {
                if (err) console.error(err);
                else localStorage.setItem('medicoConnected', 'yes');
              });
            },
            error: (err) => console.log(err) 
          })
        }

        this._stadistics.sendStatistics('inicio').catch((err) => console.log(err));
      }).catch((err: OT.OTError) => this.managePermissionsError(err) )
    }
  }

  async initiOpentokSession({ apiKey, sessionId, token, }: TokboxCredentials): Promise<void> {
    this.session = this._openTok.initSession({ apiKey, sessionId, token });

    this.session.on('signal:medicoConnected', () => {
      if (this.isMedic) return;

      this._spinner.show();

      this._openTok.publish(this.videosWrapper, this.isMedic).then(() => {
        this._spinner.hide();

        showHideWaitMedic(false);
        this.footer.nativeElement.classList.add('show');
        
        this._stadistics.sendStatistics('inicio').catch((err) => console.log(err));
      }).catch((err: OT.OTError) => this.managePermissionsError(err) );
    });

    this.session.on('streamCreated', (event) => this.streamCreated(event.stream));

    this.session.on('streamDestroyed', (event) => this.streamDestroyed(event.reason, event.stream, event.target));

    this.connectAndPublish();
  }

  streamCreated( stream: OT.Stream ) {
    if (this._dialog.dialog) this._dialog.closeDialog();
    this._openTok.suscribe(stream, this.videosWrapper);
  }

  streamDestroyed( reason: string, stream: OT.Stream, target: OT.Session ) {
    if (!this.isMedic) {
      if (reason === 'networkDisconnected') {
        this._dialog.createDialog('netM', this.cid, this.isMedic);
      } else if (reason === 'clientDisconnected') {
        this._spinner.show();
        this._videoconsulta.checkCid(this.cid).subscribe({
          next: () => {
            this._spinner.hide();
            if (stream.connection.data === 'medic') {
              this._dialog.createDialog('waitM', this.cid, this.isMedic);
            }
          },
          error: (err: HttpErrorResponse) => {
            if (err.status === 400) {
              this.videosWrapper.nativeElement.style.display = 'none';
              this.footer.nativeElement.style.display = 'none';
              this.endCallForSocio();
            }
          },
        });
      }
    } else {
      if (reason === 'networkDisconnected') {
        this._dialog.createDialog('netS', this.cid, this.isMedic);
      } else {
        if ((target as any).connections.length() < 4)
          this._dialog.createDialog('waitS', this.cid, this.isMedic);
      }
    }
  }

  private async endCallForSocio(): Promise<void> {
    await this._stadistics.sendStatistics('final', { streamfinished: 'medico' }).catch((err) => console.log(err));
    clearLocalStorage();
    this.session.unpublish(this._openTok.publisher);
    this.session.disconnect();
    this._spinner.hide();
    this.ngZone.run(() => {
      this.router.navigate(['/finalizado'], {
        state: { cid: this.cid, m: this.isMedic },
      });
    })
  }

  enableHangup(enable: boolean): void {
    if (this.isMedic) this.activeHangup = enable;
  }

  managePermissionsError(err: OT.OTError) {
    const error = err as OT.OTError & { code: number };

    if (error.code === 1500) {
      this._spinner.hide();
      this._dialog.createDialog('permissions', this.cid, this.isMedic);
    } else console.log(err);
  }
}
